import * as React from 'react';
import { FormControl, FormHelperText, Grid, Input, InputLabel, Typography } from '@material-ui/core';
import ChevronLeft from '@material-ui/icons/ChevronLeft';
import { useTranslation } from 'react-i18next';
import css from './View.module.css';
import { Button } from 'app/components/Button';
import { RootState } from 'app/store/types';
import { connect } from 'react-redux';
import { InjectedFormProps } from 'redux-form';
import { AuthorizeSsnFullPull } from 'app/components/FormAgreements/AuthorizeSsnFullPull';
import { formNameSelector } from 'app/reducers/app-config';
import { ButtonDelay } from 'app/components/ButtonDelay';
import { toFormattedSsn } from 'app/util/formatters';
import { toNormalizedSsn } from 'app/util/normalizers';

type ConnectProps = ReturnType<typeof mapStateToProps>;
type OwnProps     = { getPreviousSection?: () => void };
type Props        = ConnectProps & InjectedFormProps & OwnProps;

const mapStateToProps = (state: RootState) => ({
  formName: formNameSelector(state),
});

/**
 * Full-page SSN collection step for the Commercial Lease Municipal form.
 * Collects a single SSN for the lessee contact, stores the value in
 * `lesseeContactSsnRef`, and submits the form. SSN is intentionally kept
 * out of Redux state.
 */
export let lesseeContactSsnRef: HTMLInputElement | undefined = undefined;

const isValidSsn = (value: string): boolean => /\d{9}/.test(value);

const CollectLeaseContactSsnComponent = (props: Props) => {
  const { t } = useTranslation();
  const { submitting } = props;

  const [normalizedSsn, setNormalizedSsn] = React.useState('');

  const onChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    lesseeContactSsnRef.value = toFormattedSsn(event.target.value);
    setNormalizedSsn(toNormalizedSsn(lesseeContactSsnRef.value));
  };

  const isSsnValid = isValidSsn(normalizedSsn);

  return (
    <div style={{ padding: '16px 26px' }}>
      <Grid container>
        <Grid item xs={12}>
          <Typography variant='h6'>
            Social Security Number
          </Typography>
        </Grid>
      </Grid>
      <Grid container>
        <Grid item xs={12} classes={{ item: 'wrapper' }}>
          <FormControl error={normalizedSsn.length > 0 && !isSsnValid} fullWidth>
            <InputLabel color='primary' htmlFor='lesseeSsn'>
              {t('commercialleasemunicipal.ssn.label', { defaultValue: 'Lessee Contact SSN' })}
            </InputLabel>
            <Input
              id       = 'lesseeSsn'
              type     = 'text'
              onChange = {onChange}
              inputRef = {ref => lesseeContactSsnRef = ref}
            />
            {normalizedSsn.length > 0 && !isSsnValid && (
              <FormHelperText>Invalid social security number</FormHelperText>
            )}
          </FormControl>
        </Grid>
      </Grid>
      <AuthorizeSsnFullPull />
      <div className={css.navWrapper}>
        <div className={css.pageNav}>
          <Grid container className={css.navContainer}>
            <div className={`${css.navButton} ${css.saveLink}`}></div>
            <div className={css.navButton}>
              <Button
                disableRipple
                size      = 'small'
                className = {css.backButton}
                onClick   = {props.getPreviousSection}
              >
                <ChevronLeft /> {t('global:button.back', { defaultValue: 'Back' })}
              </Button>
            </div>
            <div className={`${css.navButton}`} style={{ marginRight: 16 }}>
              <ButtonDelay
                loading     = {submitting}
                buttonProps = {{
                  color    : 'primary',
                  disabled : !isSsnValid || submitting,
                  variant  : 'contained',
                  type     : 'submit',
                }}
              >
                {t('global:button.submit', { defaultValue: 'Submit' })}
              </ButtonDelay>
            </div>
          </Grid>
        </div>
      </div>
    </div>
  );
};

export const CollectLeaseContactSsnStep = connect(mapStateToProps)(CollectLeaseContactSsnComponent);
